
import { useState, useEffect, useRef, useCallback } from "react";
import { formatValue, shouldBeBold } from "../utils/dataFormatter";
import { getColumnIndex, getColumnLetter } from "../utils/tableHelpers";
import { saveChangesToBackend } from "../services/excelBackendService";
import { showNotification } from "../utils/notifications";

export const useTableState = ({
  financialData,
  initialColumns,
  statementId,
  companyName,
  activeStatement,
  onDataUpdate
}) => {
  const [columns, setColumns] = useState(() => initialColumns.map(c => ({ ...c })));
  const [rows, setRows] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editingHeader, setEditingHeader] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [clipboardData, setClipboardData] = useState({
    type: null,
    value: null,
    rangeData: null,
    rangeDimensions: null,
    operation: null,
    sourceRange: null
  });
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    rowIndex: null,
    columnId: null,
    isRange: false
  });

  // Column resizing state
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeGuidePosition, setResizeGuidePosition] = useState(null);

  const tableRef = useRef(null);
  const contextMenuRef = useRef(null);
  const rightClickTargetRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // Initialize rows
  useEffect(() => {
    if (financialData.length > 0) {
      const processedRows = [...financialData];
      setRows(processedRows);
    } else {
      setRows([]);
    }
  }, [financialData]);

  // Handle clicks outside context menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if this is a right-click (contextmenu event)
      if (event.type === 'contextmenu') {
        return;
      }
      
      // Don't close if clicking on the table (cells will handle their own context menu)
      if (tableRef.current?.contains(event.target)) {
        return;
      }
      
      // Close context menu if clicking outside
      if (contextMenu.visible && 
          contextMenuRef.current && 
          !contextMenuRef.current.contains(event.target)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };

    const handleRightClick = (event) => {
      // Store the target of the right-click
      rightClickTargetRef.current = event.target;
    };

    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('contextmenu', handleRightClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleRightClick);
    };
  }, [contextMenu.visible]);

  // Auto-save effect - DISABLED for manual save
  useEffect(() => {
    if (autoSaveEnabled && pendingChanges.length > 0 && !isSaving && statementId) {
      const timer = setTimeout(() => {
        saveChangesToBackendFunc();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [pendingChanges, autoSaveEnabled, isSaving, statementId]);

  // Handle column resize mouse events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || resizingColumn === null) return;
      
      const deltaX = e.clientX - resizeStartX;
      const newWidth = Math.max(50, resizeStartWidth + deltaX);
      
      // Update resize guide position
      const thElements = document.querySelectorAll('.excel-th');
      if (thElements.length > resizingColumn) {
        const thElement = thElements[resizingColumn];
        if (thElement) {
          const rect = thElement.getBoundingClientRect();
          setResizeGuidePosition(rect.right + deltaX);
        }
      }
      
      // Update column width in state
      const updatedColumns = columns.map((col, index) => {
        if (index === resizingColumn) {
          return { ...col, width: newWidth };
        }
        return col;
      });
      
      setColumns(updatedColumns);
    };
    
    const handleMouseUp = () => {
      if (isResizing && resizingColumn !== null) {
        // Save the resize action
        const columnId = columns[resizingColumn].id;
        const newWidth = columns[resizingColumn].width || 120;
        
        setEditHistory(prev => [...prev, {
          type: 'column_resize',
          columnIndex: resizingColumn,
          columnId,
          oldWidth: resizeStartWidth,
          newWidth,
          timestamp: new Date().toISOString()
        }]);
        
        setPendingChanges(prev => [...prev, {
          id: Date.now(),
          type: 'column_resize',
          columnId,
          width: newWidth,
          oldWidth: resizeStartWidth,
          timestamp: new Date().toISOString()
        }]);
        
        showNotification(`Column width set to ${Math.round(newWidth)}px`, 'info');
      }
      
      // Cleanup
      setResizingColumn(null);
      setIsResizing(false);
      setResizeGuidePosition(null);
      
      // Remove resize overlay
      const overlay = document.querySelector('.resize-overlay');
      if (overlay) overlay.remove();
      
      // Remove resize guide
      const guide = document.querySelector('.resize-guide');
      if (guide) guide.remove();
      
      // Restore text selection
      document.body.style.userSelect = '';
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizingColumn, resizeStartX, resizeStartWidth, columns]);

  // Add/remove resize guide when resizing - UPDATED
  useEffect(() => {
    if (resizeGuidePosition !== null && tableWrapperRef.current) {
      let guide = document.querySelector('.resize-guide');
      if (!guide) {
        guide = document.createElement('div');
        guide.className = 'resize-guide';
        document.body.appendChild(guide);
      }
      
      // Get table wrapper position
      const wrapperRect = tableWrapperRef.current.getBoundingClientRect();
      
      // Position guide relative to table wrapper
      guide.style.position = 'absolute';
      guide.style.left = `${resizeGuidePosition - wrapperRect.left}px`;
      guide.style.top = '0';
      guide.style.height = `${wrapperRect.height}px`;
      guide.style.width = '2px';
      guide.style.backgroundColor = '#3b82f6';
      guide.style.zIndex = '9998';
      guide.style.pointerEvents = 'none';
      
      // Make sure guide is inside wrapper
      tableWrapperRef.current.appendChild(guide);
    } else {
      const guide = document.querySelector('.resize-guide');
      if (guide && guide.parentNode) {
        guide.parentNode.removeChild(guide);
      }
    }
  }, [resizeGuidePosition]);

  // Keyboard shortcuts - REMOVED clipboard key bindings as they're now in useClipboardOperations
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S for save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      }
      
      // Escape to close context menu and clear selection
      if (e.key === 'Escape') {
        setContextMenu(prev => ({ ...prev, visible: false }));
        clearSelection();
        if (isResizing) {
          handleColumnResizeEnd();
        }
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedRange, contextMenu, isResizing]);

  // Helper functions
  const getColIndex = useCallback((columnId) => getColumnIndex(columns, columnId), [columns]);
  const getColLetter = useCallback((index) => getColumnLetter(index), []);

  // Column resize handlers
  const handleColumnResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const column = columns[columnIndex];
    setResizingColumn(columnIndex);
    setResizeStartX(e.clientX);
    setResizeStartWidth(column.width || 120);
    setIsResizing(true);
    
    // Add resize overlay
    const overlay = document.createElement('div');
    overlay.className = 'resize-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.zIndex = '9999';
    overlay.style.cursor = 'col-resize';
    document.body.appendChild(overlay);
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
  };

  const handleColumnResizeEnd = () => {
    if (isResizing && resizingColumn !== null) {
      const columnId = columns[resizingColumn].id;
      const newWidth = columns[resizingColumn].width || 120;
      
      setEditHistory(prev => [...prev, {
        type: 'column_resize',
        columnIndex: resizingColumn,
        columnId,
        oldWidth: resizeStartWidth,
        newWidth,
        timestamp: new Date().toISOString()
      }]);
      
      setPendingChanges(prev => [...prev, {
        id: Date.now(),
        type: 'column_resize',
        columnId,
        width: newWidth,
        oldWidth: resizeStartWidth,
        timestamp: new Date().toISOString()
      }]);
      
      showNotification(`Column width set to ${Math.round(newWidth)}px`, 'info');
    }
    
    // Cleanup
    setResizingColumn(null);
    setIsResizing(false);
    setResizeGuidePosition(null);
    
    // Remove resize overlay
    const overlay = document.querySelector('.resize-overlay');
    if (overlay) overlay.remove();
    
    // Remove resize guide
    const guide = document.querySelector('.resize-guide');
    if (guide && guide.parentNode) {
      guide.parentNode.removeChild(guide);
    }
    
    // Restore text selection
    document.body.style.userSelect = '';
  };

  // Reset column width
  const resetColumnWidth = (columnId) => {
    const columnIndex = getColIndex(columnId);
    if (columnIndex === -1) return;
    
    const defaultWidth = 120;
    const oldWidth = columns[columnIndex].width || 120;
    
    const updatedColumns = columns.map((col, index) => {
      if (index === columnIndex) {
        return { ...col, width: defaultWidth };
      }
      return col;
    });
    
    setColumns(updatedColumns);
    
    const change = {
      id: Date.now(),
      type: 'column_resize',
      columnId,
      width: defaultWidth,
      oldWidth,
      timestamp: new Date().toISOString()
    };
    
    setEditHistory(prev => [...prev, change]);
    setPendingChanges(prev => [...prev, change]);
    
    showNotification(`Column width reset to ${defaultWidth}px`, 'info');
  };

  // Cell selection handlers
  const handleCellMouseDown = (rowIndex, columnId, e) => {
    if (e.button !== 0) return;
    
    setIsSelecting(true);
    setSelectionStart({ rowIndex, columnId });
    setSelectedRange({
      startRow: rowIndex,
      startCol: getColIndex(columnId),
      endRow: rowIndex,
      endCol: getColIndex(columnId)
    });
    
    // Close context menu on cell click
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleCellMouseEnter = (rowIndex, columnId) => {
    if (!isSelecting || !selectionStart) return;
    
    const startColIndex = getColIndex(selectionStart.columnId);
    const currentColIndex = getColIndex(columnId);
    
    setSelectedRange({
      startRow: Math.min(selectionStart.rowIndex, rowIndex),
      startCol: Math.min(startColIndex, currentColIndex),
      endRow: Math.max(selectionStart.rowIndex, rowIndex),
      endCol: Math.max(startColIndex, currentColIndex)
    });
  };

  const handleCellMouseUp = () => setIsSelecting(false);

  const clearSelection = () => {
    setSelectedRange(null);
    setSelectionStart(null);
  };

  // Context menu handler - SIMPLIFIED
  const handleContextMenu = (e, rowIndex, columnId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const colIndex = getColIndex(columnId);
    const isRange = selectedRange && 
                    rowIndex >= selectedRange.startRow && 
                    rowIndex <= selectedRange.endRow &&
                    colIndex >= selectedRange.startCol &&
                    colIndex <= selectedRange.endCol;

    const MENU_HEIGHT = 300;
    const viewportHeight = window.innerHeight;
    let y = e.clientY;
    
    if (viewportHeight - y < MENU_HEIGHT) {
      y = viewportHeight - MENU_HEIGHT - 10;
    }

    // Position the menu, but ensure it's within viewport
    const MENU_WIDTH = 200;
    const viewportWidth = window.innerWidth;
    let x = e.clientX;
    
    if (viewportWidth - x < MENU_WIDTH) {
      x = viewportWidth - MENU_WIDTH - 10;
    }

    setContextMenu({
      visible: true,
      x,
      y,
      rowIndex,
      columnId,
      isRange
    });
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Editing functions
  const startEditingCell = (rowIndex, columnId) => {
    setEditingCell({ r: rowIndex, c: columnId });
    setEditValue(rows[rowIndex][columnId] || "");
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const finishEditing = async () => {
    if (editingCell) {
      const { r, c } = editingCell;
      const oldValue = rows[r][c];
      const newValue = editValue;
      
      const updated = [...rows];
      updated[r][c] = newValue;
      setRows(updated);
      onDataUpdate?.(updated);
      
      if (statementId) {
        const change = {
          id: Date.now(),
          type: 'edit_cell',
          row: r + 1,
          column: getColLetter(getColIndex(c)),
          value: newValue,
          oldValue,
          operation: 'edit',
          timestamp: new Date().toISOString()
        };
        setPendingChanges(prev => [...prev, change]);
        setEditHistory(prev => [...prev, change]);
      }
      
      setEditingCell(null);
    }
    
    if (editingHeader) {
      const updatedCols = columns.map(c =>
        c.id === editingHeader ? { ...c, label: editValue } : c
      );
      setColumns(updatedCols);
      setEditingHeader(null);
    }
    
    setEditValue("");
  };

  // Save to backend
  const saveChangesToBackendFunc = async () => {
    if (!statementId) {
      showNotification("No statement selected for saving", "error");
      return { success: false, error: "No statement ID" };
    }
    
    if (pendingChanges.length === 0) {
      showNotification("No changes to save", "info");
      return { success: false, error: "No changes to save" };
    }

    if (isSaving) {
      showNotification("Already saving changes, please wait", "warning");
      return { success: false, error: "Already saving" };
    }

    setIsSaving(true);
    showNotification("Saving changes to server...", "info");
    
    try {
      console.log(`📤 Saving ${pendingChanges.length} changes to backend`);
      const result = await saveChangesToBackend(statementId, pendingChanges);
      
      if (result.success) {
        setPendingChanges([]);
        setLastSaveTime(new Date());
        
        const successfulChanges = result.details?.successful_changes || pendingChanges.length;
        showNotification(`✅ Saved ${successfulChanges} changes successfully!`, "success");
        
        return { 
          success: true, 
          details: result.details,
          message: `Saved ${successfulChanges} changes`
        };
      } else {
        throw new Error(result.message || "Failed to save changes");
      }
    } catch (error) {
      console.error("❌ Save failed:", error);
      showNotification(`❌ Failed to save changes: ${error.message}`, "error");
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  };

  // Manual save function
  const handleManualSave = async () => {
    return await saveChangesToBackendFunc();
  };

  // Clear all pending changes
  const clearPendingChanges = () => {
    if (pendingChanges.length > 0) {
      setPendingChanges([]);
      showNotification(`Cleared ${pendingChanges.length} unsaved changes`, "info");
    } else {
      showNotification("No pending changes to clear", "info");
    }
  };

  // Get pending changes count
  const getPendingChangesCount = () => {
    return pendingChanges.length;
  };

  // Format functions
  const formatCellValue = useCallback((val, columnId, rowIndex) => {
    return formatValue(val, columnId, rowIndex, columns);
  }, [columns]);

  const checkBold = useCallback((value, rowIndex, columnIndex) => {
    return shouldBeBold(value, rowIndex, columnIndex);
  }, []);

  const showNotif = useCallback((message, type = "info") => {
    showNotification(message, type);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (financialData && financialData.length > 0) {
          console.log(`Loading ${financialData.length} rows of financial data`);
          
          // Clean the data to remove NaN/Infinity values
          const cleanedData = financialData.map(row => {
            const cleanedRow = {};
            Object.keys(row).forEach(key => {
              let value = row[key];
              
              // Handle NaN and Infinity
              if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
                value = "";
              }
              
              // Handle null/undefined
              if (value === null || value === undefined) {
                value = "";
              }
              
              cleanedRow[key] = value;
            });
            return cleanedRow;
          });
          
          setRows(cleanedData);
          console.log(`Successfully loaded ${cleanedData.length} cleaned rows`);
        } else {
          console.warn("No financial data provided or empty array");
          setRows([]);
        }
      } catch (error) {
        console.error("Error loading financial data:", error);
        setRows([]);
        showNotification("Error loading table data. Please refresh.", "error");
      }
    };
    
    loadData();
  }, [financialData]);

  return {
    // State
    columns, setColumns,
    rows, setRows,
    editingCell, setEditingCell,
    editingHeader, setEditingHeader,
    editValue, setEditValue,
    isSaving, setIsSaving,
    pendingChanges, setPendingChanges,
    lastSaveTime, setLastSaveTime,
    editHistory, setEditHistory,
    selectedRange, setSelectedRange,
    isSelecting, setIsSelecting,
    clipboardData, setClipboardData,
    contextMenu, setContextMenu,
    
    // Column resizing state
    resizingColumn, setResizingColumn,
    resizeStartX, setResizeStartX,
    resizeStartWidth, setResizeStartWidth,
    isResizing, setIsResizing,
    resizeGuidePosition, setResizeGuidePosition,
    
    // Refs
    tableRef,
    contextMenuRef,
    tableWrapperRef,
    
    // Handlers
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    clearSelection,
    handleContextMenu,
    closeContextMenu,
    startEditingCell,
    finishEditing,
    saveChangesToBackend: saveChangesToBackendFunc,
    handleManualSave,
    clearPendingChanges,
    getPendingChangesCount,
    
    // Column resizing handlers
    handleColumnResizeStart,
    handleColumnResizeEnd,
    resetColumnWidth,
    
    // Utility functions
    formatValue: formatCellValue,
    shouldBeBold: checkBold,
    showNotification: showNotif,
    getColumnIndex: getColIndex,
    getColumnLetter: getColLetter,
    
    // For parent component
    onDataUpdate
  };
};
